import "@/App.css";
import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { bootstrapNative } from "@/lib/native";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import FamilyDashboard from "@/pages/FamilyDashboard";
import FamilyProfile from "@/pages/FamilyProfile";
import ChildProfile from "@/pages/ChildProfile";
import SitterDashboard from "@/pages/SitterDashboard";
import SitterProfileEdit from "@/pages/SitterProfileEdit";
import SearchSitters from "@/pages/SearchSitters";
import SitterDetail from "@/pages/SitterDetail";
import BookingsList from "@/pages/BookingsList";
import Messages from "@/pages/Messages";
import AdminDashboard from "@/pages/AdminDashboard";
import TwoFactorSettings from "@/pages/TwoFactorSettings";
import IncidentsPage from "@/pages/IncidentsPage";
import Pricing from "@/pages/Pricing";
import SubscriptionSettings from "@/pages/SubscriptionSettings";
import FavoritesPage from "@/pages/FavoritesPage";
import InsightsPage from "@/pages/InsightsPage";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AccountSettings from "@/pages/AccountSettings";
import FamilyListings from "@/pages/FamilyListings";
import SitterListingsBrowse from "@/pages/SitterListingsBrowse";
import SitterListingDetail from "@/pages/SitterListingDetail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import InviteAccept from "@/pages/InviteAccept";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/AdminBlog";
import AdminInvoices from "@/pages/AdminInvoices";
import SitterInvoices from "@/pages/SitterInvoices";
import FamilyInvoices from "@/pages/FamilyInvoices";
import ChangePasswordRequired from "@/pages/ChangePasswordRequired";
import AdminFamilyDetail from "@/pages/AdminFamilyDetail";
import SitterFamilyView from "@/pages/SitterFamilyView";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import HelpWidget from "@/components/HelpWidget";
import AnnouncementBanner from "@/components/AnnouncementBanner";

function IndexRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (user.role === "family") return <Navigate to="/family" replace />;
  if (user.role === "sitter") return <Navigate to="/sitter" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return <Landing />;
}

function App() {
  useEffect(() => { bootstrapNative(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors offset={80} />
        <AnnouncementBanner />
        <Routes>
          <Route path="/" element={<IndexRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password-required" element={<ProtectedRoute><ChangePasswordRequired /></ProtectedRoute>} />
          <Route path="/register" element={<Register />} />
          <Route path="/invite/:token" element={<InviteAccept />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Family */}
          <Route path="/family" element={<ProtectedRoute role="family"><FamilyDashboard /></ProtectedRoute>} />
          <Route path="/family/profile" element={<ProtectedRoute role="family"><FamilyProfile /></ProtectedRoute>} />
          <Route path="/family/children/:id" element={<ProtectedRoute role="family"><ChildProfile /></ProtectedRoute>} />
          <Route path="/family/listings" element={<ProtectedRoute role="family"><FamilyListings /></ProtectedRoute>} />

          {/* Listings (sitter browse) */}
          <Route path="/listings" element={<ProtectedRoute role="sitter"><SitterListingsBrowse /></ProtectedRoute>} />
          <Route path="/listings/:id" element={<ProtectedRoute role="sitter"><SitterListingDetail /></ProtectedRoute>} />

          {/* Sitter */}
          <Route path="/sitter" element={<ProtectedRoute role="sitter"><SitterDashboard /></ProtectedRoute>} />
          <Route path="/sitter/profile" element={<ProtectedRoute role="sitter"><SitterProfileEdit /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />

          {/* Shared */}
          <Route path="/search" element={<SearchSitters />} />
          <Route path="/sitters/:id" element={<SitterDetail />} />
          <Route path="/bookings" element={<ProtectedRoute><BookingsList /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/incidents" element={<ProtectedRoute><IncidentsPage /></ProtectedRoute>} />
          <Route path="/settings/2fa" element={<ProtectedRoute><TwoFactorSettings /></ProtectedRoute>} />
          <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionSettings /></ProtectedRoute>} />
          <Route path="/settings/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/favorites" element={<ProtectedRoute role="family"><FavoritesPage /></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute role="family"><InsightsPage /></ProtectedRoute>} />
          <Route path="/admin/promo-codes" element={<ProtectedRoute role="admin"><AdminPromoCodes /></ProtectedRoute>} />
          <Route path="/admin/blog" element={<ProtectedRoute role="admin"><AdminBlog /></ProtectedRoute>} />
          <Route path="/admin/invoices" element={<ProtectedRoute role="admin"><AdminInvoices /></ProtectedRoute>} />
          <Route path="/admin/families/:userId" element={<ProtectedRoute role="admin"><AdminFamilyDetail /></ProtectedRoute>} />
          <Route path="/family/:userId/view" element={<ProtectedRoute role="sitter"><SitterFamilyView /></ProtectedRoute>} />
          <Route path="/sitter/invoices" element={<ProtectedRoute role="sitter"><SitterInvoices /></ProtectedRoute>} />
          <Route path="/family/invoices" element={<ProtectedRoute role="family"><FamilyInvoices /></ProtectedRoute>} />

          {/* Payment */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <HelpWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
